import i18n from '@/lib/i18n';
import { getResourceMeta } from '@/features/billing/constants/resource-registry';

/**
 * Entitlement / billing domain error codes (50xxx) emitted by the backend
 * billing runtime. These can gate ANY mutation across the app (contacts, tags,
 * segments, seats, SMS, AI), so the code→friendly-message mapping lives here in
 * the shared lib rather than inside the billing feature. Anything unmapped
 * returns undefined so callers fall through to their normal error handling.
 */
export const ENTITLEMENT_ERROR_CODE = {
    CREDITS_INSUFFICIENT: 50001,
    LIMIT_REACHED: 50002,
    FEATURE_NOT_INCLUDED: 50003,
    SUBSCRIPTION_LOCKED: 50004,
    NO_SUBSCRIPTION: 50005,
    PAYMENT_OVERDUE: 50011,
    BILLING_UNAVAILABLE: 50013,
} as const;

/** Pull the numeric domain error code off an API error body (nested or flat). */
export function extractErrorCode(data: unknown): number | undefined {
    if (!data || typeof data !== 'object') return undefined;
    const d = data as Record<string, unknown>;
    if (typeof d.errorCode === 'number') return d.errorCode;
    // Some responses nest the domain error under `error` (ApiResponse wrapper).
    if (d.error && typeof d.error === 'object') {
        const inner = (d.error as Record<string, unknown>).errorCode;
        if (typeof inner === 'number') return inner;
    }
    return undefined;
}

/** Pull the domain error `details` object (the super-admin decision / guard spec). */
export function extractErrorDetails(data: unknown): Record<string, unknown> | undefined {
    if (!data || typeof data !== 'object') return undefined;
    const d = data as Record<string, unknown>;
    if (d.error && typeof d.error === 'object') {
        const details = (d.error as Record<string, unknown>).details;
        if (details && typeof details === 'object') return details as Record<string, unknown>;
    }
    if (d.details && typeof d.details === 'object') return d.details as Record<string, unknown>;
    return undefined;
}

/**
 * Resolve which resource a limit error was about. The consume path carries
 * `details.resourceKey`; the coarse guard path carries `details.spec.resource`.
 */
function resourceKeyFromDetails(details: Record<string, unknown> | undefined): string | undefined {
    if (!details) return undefined;
    if (typeof details.resourceKey === 'string') return details.resourceKey;
    const spec = details.spec;
    if (spec && typeof spec === 'object') {
        const resource = (spec as Record<string, unknown>).resource;
        if (typeof resource === 'string') return resource;
    }
    return undefined;
}

export function unavailableMessage(): string {
    return i18n.t(
        'billing.errors.unavailable',
        'Billing is temporarily unavailable — your action was not processed. Try again shortly.',
    );
}

/**
 * Limit-reached copy, resource-named when the resource is known. Leads with
 * "Plan limit exceeded" on purpose: this rejection is never something the
 * user can fix by editing their input, so it must not read like a validation
 * error — it names the cap and points at the two ways out of it.
 */
function limitReachedMessage(data: unknown): string {
    const resourceKey = resourceKeyFromDetails(extractErrorDetails(data));
    if (resourceKey) {
        return i18n.t('billing.errors.limitReachedResource', {
            resource: getResourceMeta(resourceKey).label,
            defaultValue:
                "Plan limit exceeded — you've reached your plan's {{resource}} limit. Upgrade your plan or buy an add-on to get more.",
        });
    }
    return i18n.t(
        'billing.errors.limitReached',
        'Plan limit exceeded — you have reached a limit set by your current subscription plan. Upgrade your plan or buy an add-on to increase it.',
    );
}

/**
 * Friendly, human-readable message for a known entitlement error code on an API
 * error body. Returns undefined for anything that isn't a 50xxx entitlement
 * error so the caller can fall through to its normal (raw-message) handling.
 */
export function friendlyEntitlementMessage(data: unknown): string | undefined {
    const code = extractErrorCode(data);
    if (code === undefined) return undefined;
    switch (code) {
        case ENTITLEMENT_ERROR_CODE.CREDITS_INSUFFICIENT:
            return i18n.t(
                'billing.errors.creditsInsufficient',
                'Not enough credits — buy a credit pack to continue.',
            );
        case ENTITLEMENT_ERROR_CODE.LIMIT_REACHED:
            return limitReachedMessage(data);
        case ENTITLEMENT_ERROR_CODE.FEATURE_NOT_INCLUDED:
            return i18n.t(
                'billing.errors.featureNotIncluded',
                'Your plan does not include this feature — upgrade to unlock it.',
            );
        case ENTITLEMENT_ERROR_CODE.SUBSCRIPTION_LOCKED:
            return i18n.t(
                'billing.errors.subscriptionLocked',
                'Your subscription is locked — please check your payment method.',
            );
        case ENTITLEMENT_ERROR_CODE.NO_SUBSCRIPTION: {
            // "Unknown organization" is the SA org-directory miss (org not
            // mirrored into billing yet), NOT a missing subscription — the
            // choose-a-plan copy would gaslight a first-time buyer whose
            // failing action IS choosing a plan.
            const reason = extractErrorDetails(data)?.reason;
            if (reason === 'Unknown organization') {
                return i18n.t(
                    'billing.errors.orgUnknown',
                    "We couldn't find your organization in the billing system — please try again in a moment or contact support.",
                );
            }
            return i18n.t(
                'billing.errors.noSubscription',
                'No active subscription — choose a plan to get started.',
            );
        }
        case ENTITLEMENT_ERROR_CODE.PAYMENT_OVERDUE:
            return i18n.t(
                'billing.errors.paymentOverdue',
                'Your payment is overdue — update your payment method to restore service.',
            );
        case ENTITLEMENT_ERROR_CODE.BILLING_UNAVAILABLE:
            return unavailableMessage();
        default:
            return undefined;
    }
}
